import {
  BlockMapBuilder,
  ContentState,
  EditorState,
  convertToRaw,
  Modifier,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { stateFromHTML } from "draft-js-import-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useTranslation } from "react-i18next"; 
import "./styles.css";  
import GeneralMessage from "../../Common/GeneralMessage";

interface RichTextEditorProps {
  onChange: (value: string) => void;
  label: string;
  className?: string;
  EditorClass?: string;
  labelClass?: string;
  message?: string;
  disabled?: boolean;
  initialContent?: string;
}

const isEmptyHtml = (html: string): boolean => {
  if (!html || html.trim() === "") return true;

  if (typeof window === "undefined") {
    return html.replace(/<[^>]*>/g, "").trim() === "";
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || "";
  return text.trim() === "";
};

// normalize "rgb(26, 188, 156)" -> "rgb(26,188,156)"
const normalizeColor = (c: string) => c.replace(/\s+/g, "").toLowerCase();

const extractColorFromElement = (el: HTMLElement): string | null => {
  // Prefer computed style from style attribute
  const direct = el.style?.color;
  if (direct) return normalizeColor(direct);

  // Fallback: parse style attribute
  const styleAttr = el.getAttribute("style") || "";
  const match = styleAttr.match(/color\s*:\s*([^;]+)/i);
  if (match?.[1]) return normalizeColor(match[1].trim());

  return null;
};

// Import HTML -> ContentState AND collect needed color styles for customStyleMap
const contentStateFromHtmlWithColors = (html: string) => {
  const colors = new Set<string>();

  if (typeof window === "undefined" || isEmptyHtml(html)) {
    return { contentState: ContentState.createFromText(""), colors };
  }

  const contentState = stateFromHTML(html, {
    customInlineFn: (element, { Style }) => {
      const el = element as HTMLElement;

      // Catch <span style="color: ...">
      const color = extractColorFromElement(el);
      if (color) {
        // IMPORTANT: use react-draft-wysiwyg expected key format:
        // "color-rgb(26,188,156)" or "color-#1abc9c"
        const key = `color-${color}`;
        colors.add(color);
        return Style(key);
      }

      return undefined;
    },
  });

  return { contentState, colors };
};

const editorStateToHtml = (state: EditorState): string => {
  const contentState = state.getCurrentContent();
  if (!contentState.hasText()) return "";

  const raw = convertToRaw(contentState);
  const html = draftToHtml(raw);
  return isEmptyHtml(html) ? "" : html;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  onChange,
  label,
  className = "",
  EditorClass,
  labelClass,
  disabled,
  message,
  initialContent = "",
}) => {
  const { t } = useTranslation();

  const [customStyleMap, setCustomStyleMap] = useState<
    Record<string, React.CSSProperties>
  >({});

  // init once
  const init = useMemo(() => {
    const { contentState, colors } =
      contentStateFromHtmlWithColors(initialContent);

    const map: Record<string, React.CSSProperties> = {};
    colors.forEach((c) => {
      map[`color-${c}`] = { color: c };
    });

    return { editorState: EditorState.createWithContent(contentState), map };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editorState, setEditorState] = useState<EditorState>(init.editorState);

  useEffect(() => {
    setCustomStyleMap((prev) => ({ ...prev, ...init.map }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avoid clobbering edits
  const lastInitialRef = useRef<string>(initialContent);

  useEffect(() => {
    if (initialContent === lastInitialRef.current) return;
    lastInitialRef.current = initialContent;

    // only overwrite if editor is empty
    if (!editorState.getCurrentContent().hasText()) {
      const { contentState, colors } =
        contentStateFromHtmlWithColors(initialContent);

      const map: Record<string, React.CSSProperties> = {};
      colors.forEach((c) => {
        map[`color-${c}`] = { color: c };
      });

      setCustomStyleMap((prev) => ({ ...prev, ...map }));
      setEditorState(EditorState.createWithContent(contentState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContent]);

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
    onChange(editorStateToHtml(state));
  };

  const handlePastedText = (
    text: string,
    html: string | undefined,
    state: EditorState,
  ) => {
    const selection = state.getSelection();
    const content = state.getCurrentContent();

    if (html) {
      try {
        const { contentState, colors } = contentStateFromHtmlWithColors(html);

        const map: Record<string, React.CSSProperties> = {};
        colors.forEach((c) => {
          map[`color-${c}`] = { color: c };
        });
        if (Object.keys(map).length) {
          setCustomStyleMap((prev) => ({ ...prev, ...map }));
        }

        const fragment = BlockMapBuilder.createFromArray(
          contentState.getBlocksAsArray(),
        );
        const withFragment = Modifier.replaceWithFragment(
          content,
          selection,
          fragment,
        );

        const nextState = EditorState.push(
          state,
          withFragment,
          "insert-fragment",
        );
        setEditorState(nextState);
        onChange(editorStateToHtml(nextState));
        return true;
      } catch (e) {
        console.warn("Error pasting HTML, falling back to plain text:", e);
      }
    }

    const nextContent = selection.isCollapsed()
      ? Modifier.insertText(content, selection, text)
      : Modifier.replaceText(content, selection, text);

    const nextState = EditorState.push(state, nextContent, "insert-characters");
    setEditorState(nextState);
    onChange(editorStateToHtml(nextState));
    return true;
  };

  return (
    <div
      className={`mx-auto flex h-full w-[99%] flex-col gap-2 rounded-lg ${className}`}
    >
      <div className="flex lg:items-center justify-between gap-3 lg:flex-row flex-col">
        <h2 className={`${labelClass || "text-lg font-bold"}`}>{label}</h2>
        {message && <GeneralMessage status="warning" message={message} />}
      </div>

      <Editor
        readOnly={disabled}
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        handlePastedText={handlePastedText}
        toolbarClassName="toolbar"
        wrapperClassName={`wrapper ${EditorClass || ""}`}
        editorClassName="editor"
        placeholder={t("type_here")}
        customStyleMap={customStyleMap}
        toolbar={{
          options: [
            "inline",
            "fontSize",
            "fontFamily",
            "list",
            "textAlign",
            "colorPicker",
            "link",
            "emoji",
            "remove",
          ],
          inline: { options: ["bold", "italic", "underline", "strikethrough"] },
          fontSize: {
            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
          },
          fontFamily: {
            options: [
              "Arial",
              "Georgia",
              "Impact",
              "Tahoma",
              "Times New Roman",
              "Verdana",
            ],
          },
        }}
      />
    </div>
  );
};

export default RichTextEditor; 